import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { amplitudeContextSelector } from '@/features/amplitude/store/amplitude';
import { Provider } from '@/features/game-context/types';
import { getErrorType } from '@/features/game-window-integration/helpers/error-mapping.ts';
import {
  PragmaticEventData,
  PragmaticEvents,
} from '@/features/game-window-integration/providers/pragmatic/types';
import {
  ProviderLoggerActionNames,
  ProviderLoggerMessages,
} from '@/features/game-window-integration/types';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';
import { processPayload } from '@/utils/process-error-payload';

export const usePragmaticHandlers = (): {
  handleGameReady: () => void;
  handleSpinInProgress: (data: PragmaticEventData) => void;
  handleShowDeposit: () => void;
  handleCloseGame: () => void;
  handleNotImplemented: () => void;
  handleError: (data: PragmaticEventData) => void;
  handleVolumeChange: (data: PragmaticEventData) => void;
  handleGameChange: (data: PragmaticEventData) => void;
  getGameProviderFrame: () => Window | null | undefined;
} => {
  const getGameProviderFrame = useCallback(
    () =>
      document.querySelector<HTMLIFrameElement>('#game-client')?.contentWindow,
    [],
  );

  const { gameSwitchEnabled } = useSelector(amplitudeContextSelector);

  const handleGameReady = useCallback(() => {
    setTimeout(() => {
      WrapperProxy.setGameLoaded();
    }, 1000);
  }, []);

  const handleSpinInProgress = useCallback((data: PragmaticEventData) => {
    const { name } = data ?? {};

    if (!name) return;
    WrapperProxy.setSpinInProgress(name === PragmaticEvents.ROUND_START);
  }, []);

  const handleShowDeposit = useCallback(() => {
    WrapperProxy.showFunds();
  }, []);

  const handleCloseGame = useCallback(() => {
    WrapperProxy.setGameLauncherClosed();
  }, []);

  const handleVolumeChange = useCallback((data: PragmaticEventData) => {
    const { event } = data ?? {};

    if (!event) return;
    WrapperProxy.setAudio(event === PragmaticEvents.SOUND_ON);
  }, []);

  const handleNotImplemented = useCallback(() => {
    Logger.rumAction({
      name: ProviderLoggerMessages.NOT_IMPLEMENTED,
      useCase: DdEventCategory.ProviderIntegration,
      eventType: DdEventType.Info,
      context: {
        provider: Provider.PRAGMATIC,
      },
    });
  }, []);

  const handleError = useCallback((payload: PragmaticEventData) => {
    const { name, event, params } = payload ?? {};

    if (event === PragmaticEvents.SYSTEM_MESSAGE_SHOWN) {
      const errorCode = params?.msg_code ?? params?.type;

      if (errorCode) {
        const errorType = getErrorType(
          errorCode,
          Provider.PRAGMATIC,
          false,
          payload,
        );
        const errorMessage = getErrorType(errorCode, Provider.PRAGMATIC, true);

        WrapperProxy.setProviderError({
          display: true,
          errorCode,
          errorMessage,
          errorType,
        });
        return;
      }
    }

    if (name) {
      const errorType = getErrorType(name, Provider.PRAGMATIC, false, payload);

      WrapperProxy.setProviderError({
        display: true,
        errorCode: name,
        errorMessage: '',
        errorType,
      });
    }
  }, []);

  const handleGameChange = useCallback(
    (data: PragmaticEventData) => {
      Logger.rumAction({
        name: ProviderLoggerActionNames.GAME_SWITCH,
        useCase: DdEventCategory.ProviderIntegration,
        description: ProviderLoggerMessages.GAME_SWITCH,
        eventType: DdEventType.Info,
        context: {
          provider: Provider.PRAGMATIC,
          rgsCode: data.params?.gameCode,
          stringifiedPayload: processPayload(data),
        },
      });

      WrapperProxy.setGameSwitch(
        {
          rgsCode: data.params?.gameCode as string,
          provider: Provider.PRAGMATIC,
        },
        gameSwitchEnabled ?? false,
      );
    },
    [gameSwitchEnabled],
  );

  return {
    handleGameReady,
    handleSpinInProgress,
    handleShowDeposit,
    handleCloseGame,
    handleNotImplemented,
    handleVolumeChange,
    handleError,
    getGameProviderFrame,
    handleGameChange,
  };
};
